<script lang="ts" setup>
import { Collapse } from "bootstrap";
import { onMounted, watch, ref } from "vue";

type Props = {
  name: string;
  parentId?: string;
  headingSize?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  expanded?: boolean;
  showChevron?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  parentId: undefined,
  headingSize: "h6",
  expanded: false,
});

const emit = defineEmits(["update:expanded"]);

const collapseElement = ref<HTMLElement | null>(null);
let collapseInstance: Collapse | null = null;

const isExpanded = ref(props.expanded);

onMounted(() => {
  if (collapseElement.value) {
    collapseInstance = new Collapse(collapseElement.value, {
      toggle: false,
    });

    collapseElement.value.addEventListener("show.bs.collapse", () => {
      isExpanded.value = true;
      emit("update:expanded", true);
    });

    collapseElement.value.addEventListener("hide.bs.collapse", () => {
      isExpanded.value = false;
      emit("update:expanded", false);
    });

    if (props.expanded) {
      collapseInstance.show();
    }
  }
});

watch(
  () => isExpanded.value,
  (newValue) => {
    if (collapseInstance) {
      if (newValue) {
        collapseInstance.show();
      } else {
        collapseInstance.hide();
      }
    }
  }
);
</script>

<template>
  <div class="accordion-item">
    <component
      :is="headingSize"
      :id="`heading-${name}`"
      class="accordion-header"
    >
      <button
        class="accordion-button"
        :class="{
          collapsed: !isExpanded,
          'd-flex': showChevron,
          'align-items-center': showChevron,
          'justify-content-center': showChevron,
        }"
        type="button"
        data-bs-toggle="collapse"
        :data-bs-target="`#collapse-${name}`"
        :aria-expanded="isExpanded"
        :aria-controls="`collapse-${name}`"
      >
        <slot name="title" /><BaseIcon
          v-if="showChevron"
          icon-name="chevron-up"
          class="accordion-chevron"
          :class="{ 'accordion-chevron__expanded': isExpanded }"
        />
      </button>
    </component>
    <div
      :id="`collapse-${name}`"
      ref="collapseElement"
      class="accordion-collapse collapse"
      :aria-labelledby="`heading-${name}`"
      :data-bs-parent="parentId ? `#${parentId}` : undefined"
    >
      <div class="accordion-body" :class="{ 'mt-4': showChevron }">
        <slot />
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.accordion {
  &--noborder .accordion-button {
    padding: $spacer-3 0;
  }

  &--noborder .accordion-body {
    padding: 0 0 $spacer-3;
  }

  &--gray .accordion-body {
    color: $secondary;
  }

  .accordion-button {
    font-weight: 500;

    &:focus-visible {
      outline: none;
      box-shadow:
        $shadow-brand,
        0 0 0 1px $primary;
    }
  }

  .accordion-button:not(.collapsed, :focus-visible) {
    box-shadow: none;
  }

  &-chevron {
    transform: rotate(0deg);
    transition: transform 0.3s ease;
    margin-left: $spacer-2;

    &__expanded {
      transform: rotate(180deg);
    }
  }

  &--noborder .accordion-item {
    border: none;
    border-top: 1px solid $border-color;
  }

  &--border {
    border-radius: $border-radius-lg;

    // border: 1px solid $border-color;
    box-shadow: $shadow-brand;
  }
}
</style>
